import { useMutation } from "@tanstack/react-query";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { put } from "api/api-methods";
import { useBookingId } from "hooks/use-booking-id";
import { useError } from "hooks/use-error";
import { error as logError, trackEvent } from "logger/logger";
import { pageTrackingService, TRACKING_VERSION } from "logger/page-tracking";
import queryClient from "query-client";
import { State, Time } from "store/store-types";
import { Contact } from "types/contact";
import {
    removeTime,
    UpdateReservationParameters as UpdateBookingParameters,
} from "types/request-parameters";
import { WebBooking } from "types/web-booking";

export const useUpdateBooking = () => {
    const { setErrorCode, openReservationFailedModal } = useError();
    const id = useBookingId();
    const { data: booking } = useWebBooking();

    const updateBooking = async ({
        timesToUpdate,
        removeTimes,
        guests,
        messageOverride,
        contact = {} as Contact,
        isPending = true,
        isInquiry = false,
    }: {
        state: State;
        timesToUpdate: Time[];
        removeTimes?: removeTime[];
        guests?: number;
        messageOverride?: string;
        contact?: Contact;
        isPending?: boolean;
        isInquiry?: boolean;
    }) => {
        if (!booking) {
            throw new Error("Booking data is required for updating booking");
        }

        const message = messageOverride ? messageOverride : booking.message;
        const data: UpdateBookingParameters = new UpdateBookingParameters(
            {
                date: booking.start.clone().startOf("day"),
                guests: guests ? guests : booking.guests,
                guestsChildren: booking.guestsChildren,
                contact,
            },
            timesToUpdate,
            removeTimes,
            message,
            isPending,
            isInquiry
        );

        const startTime = new Date().getTime();

        try {
            const booking = await put<WebBooking, UpdateBookingParameters>(
                `WebBooking/WebBookings/${id}`,
                data
            );
            await queryClient.refetchQueries({
                // Todo: We refetch it because the updated response does not include the webrule. Should fix this on the backend.
                queryKey: ["booking", id],
            });

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            const pageContext = pageTrackingService.getCurrentContext();

            // Track booking update success
            trackEvent("Booking.Updated.Success", {
                bookingId: id ?? "",
                guests: guests ?? booking.guests,
                timesUpdatedCount: timesToUpdate.length,
                timesRemovedCount: removeTimes?.length ?? 0,
                isPending,
                isInquiry,
                sessionId: pageContext.sessionId,
                durationMs: loadTime,
                trackingVersion: TRACKING_VERSION,
            });

            return booking;
        } catch (error) {
            const response = (error as { response?: { data?: { code: number } } })?.response?.data;
            logError(error);
            const errorCode = response?.code;
            if (typeof errorCode === "number") {
                setErrorCode(errorCode);
                openReservationFailedModal();
            }

            const endTime = new Date().getTime();
            const loadTime = endTime - startTime;
            const pageContext = pageTrackingService.getCurrentContext();

            // Track booking update failure
            trackEvent("Booking.Updated.Failure", {
                bookingId: id ?? "",
                guests: guests ?? booking.guests,
                timesUpdatedCount: timesToUpdate.length,
                timesRemovedCount: removeTimes?.length ?? 0,
                isPending,
                isInquiry,
                sessionId: pageContext.sessionId,
                durationMs: loadTime,
                errorMessage: error instanceof Error ? error.message : "Unknown error",
                trackingVersion: TRACKING_VERSION,
            });

            throw error;
        }
    };

    return useMutation({
        mutationFn: updateBooking,
        scope: {
            id: "webbooking-create",
        },
    });
};
